/**
 * Configuración de impuestos y cargos para el sistema Macartur
 *
 * IMPORTANTE: Los precios en el sistema YA INCLUYEN IGV y otros cargos.
 * Esta configuración permite calcular el desglose para mostrar en UI y reportes.
 */

export interface TaxConfig {
  /** Porcentaje de IGV (ejemplo: 0.13 para 13%) */
  igvRate: number;
  /** Nombre del impuesto para mostrar en UI */
  igvDisplayName: string;

  /** Porcentaje de otros cargos (ejemplo: 0.10 para 10%) */
  otherChargesRate: number;
  /** Nombre de otros cargos para mostrar en UI */
  otherChargesDisplayName: string;
}

/**
 * Configuración actual de impuestos y cargos
 * Estos valores deben coincidir con fiscal_parameters en la BD
 */
export const TAX_CONFIG: TaxConfig = {
  igvRate: 0.13, // 13% IGV (IGV_PERCENT en fiscal_parameters)
  igvDisplayName: 'IGV',

  otherChargesRate: 0.10, // 10% Otros Cargos (OTHER_CHARGES_PERCENT en fiscal_parameters)
  otherChargesDisplayName: 'Otros Cargos'
};

/**
 * Utilidades para cálculos de impuestos y cargos
 *
 * LÓGICA: Los precios YA INCLUYEN impuestos.
 * - El total = suma de precios (no se suma nada más)
 * - El desglose se calcula hacia atrás para mostrar valorBase, IGV, otrosCargos
 */
export class TaxCalculator {
  /**
   * Calcula el desglose de un total que YA INCLUYE impuestos
   *
   * Fórmula:
   * - divisor = 1 + igvRate + otherChargesRate
   * - valorBase = totalConImpuestos / divisor
   * - igv = valorBase * igvRate
   * - otrosCargos = valorBase * otherChargesRate
   */
  static calculateBreakdown(totalConImpuestos: number, discount: number = 0) {
    const totalAfterDiscount = totalConImpuestos - discount;

    const divisor = 1 + TAX_CONFIG.igvRate + TAX_CONFIG.otherChargesRate;
    const valorBase = totalAfterDiscount / divisor;
    const igv = valorBase * TAX_CONFIG.igvRate;
    const otherCharges = valorBase * TAX_CONFIG.otherChargesRate;

    return {
      subtotal: Math.round(valorBase * 100) / 100,
      igv: Math.round(igv * 100) / 100,
      otherCharges: Math.round(otherCharges * 100) / 100,
      total: Math.round(totalAfterDiscount * 100) / 100
    };
  }

  /**
   * Obtiene el porcentaje de IGV para mostrar en UI
   */
  static getIgvPercentage(): string {
    return `${(TAX_CONFIG.igvRate * 100).toFixed(1)}%`;
  }

  /**
   * Obtiene el nombre del IGV para mostrar
   */
  static getIgvDisplayName(): string {
    return `${TAX_CONFIG.igvDisplayName} (${this.getIgvPercentage()})`;
  }

  /**
   * Obtiene el porcentaje de otros cargos para mostrar en UI
   */
  static getOtherChargesPercentage(): string {
    return `${(TAX_CONFIG.otherChargesRate * 100).toFixed(1)}%`;
  }

  /**
   * Obtiene el nombre de otros cargos para mostrar
   */
  static getOtherChargesDisplayName(): string {
    return `${TAX_CONFIG.otherChargesDisplayName} (${this.getOtherChargesPercentage()})`;
  }

  // Métodos legacy para compatibilidad (deprecated)
  static calculateIgv(subtotal: number): number {
    return this.calculateBreakdown(subtotal).igv;
  }

  static calculateOtherCharges(subtotal: number): number {
    return this.calculateBreakdown(subtotal).otherCharges;
  }

  static calculateTotal(subtotal: number, discount: number = 0): number {
    // El subtotal YA ES el total (precios incluyen impuestos)
    return Math.round((subtotal - discount) * 100) / 100;
  }
}
